import React, { useState } from 'react';
import { Button, Col, Form, message, Modal, Space } from 'antd';
import { BookyInput, InputType } from '../Forms/BookyInput';
import { TextService } from '../../Services/TextService';
import { WebApiMemberDtoGroupDto, WebApiMemberDtoMemberDto, WebApiModelsExternalPayment, WebApiModelsPayment } from '../../Repository/eventbookyapi';
import { MemberService } from '../../Services/MemberService';
import { BookyButton } from '../Buttons/BookyButton';
import { BookySelectDropDown } from '../Forms/BookySelectDropDown';
import { getCurrentYearAsOption, getLastThreeYearAsOptions } from '../../Helpers/PaymentYearOptions';
import { getCurrentYearAsString, getPreviousYear } from '../../Helpers/DateHelpers';
import { OneFrontDatePicker } from '../Forms/BookyDatePicker';
import moment from 'moment';
import { IntegrationService } from '../../Services/IntegrationService';
import { LinkOutlined, FilePdfOutlined } from '@ant-design/icons';
import { downloadBlobFile, getFileNameFromContentDisposition } from '../../Helpers/FileDownloadHelper';

interface AddPaymentLinkProps {
    loadMembers?: ((refresh: boolean) => void) | (() => void);
    groups?: WebApiMemberDtoGroupDto[];
    member: WebApiMemberDtoMemberDto;
}

export const AddPaymentLink = (props: AddPaymentLinkProps) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const GetText = TextService.GetText;
    const [payments, setPayments] = useState([] as WebApiModelsPayment[]);
    const [externalPayments, setExternalPayments] = useState([] as WebApiModelsExternalPayment[]);
    const [newlyCreatedUri, setnNewlyCreatedUri] = useState(null as string | null);
    const [paymentLinkId, setPaymentLinkId] = useState(null as string | null);
    const [isLoading, setIsLoading] = useState(false as boolean);
    const onCancel = () => {
        setnNewlyCreatedUri(null);
        setPaymentLinkId(null);
        form.setFieldValue("groupName", "");
        setOpen(false);
        if (props.loadMembers) {
            props.loadMembers.length > 0 ? props.loadMembers(false) : (props.loadMembers as () => void)();
        }
    };

    const onCreate = async () => {
        if (!props.member.memberId) return;

        try {
            setIsLoading(true)
            var response = await IntegrationService.CreatePaymentLink({
                memberId: Number(props.member.memberId),
                description: form.getFieldValue("description").toString(),
                amount: form.getFieldValue("amount"),
                paymentType: 1,
                connectedYear: form.getFieldValue("year").toString(),
            });

            setnNewlyCreatedUri(response.checkoutUrl!);
            setPaymentLinkId(response.id!);
            form.setFieldValue("text", "");
        } catch (error) {
            message.warning(GetText("EditMember_ErrorSaving"));
            return;
        }
        finally {
            setIsLoading(false)
        }
    }

    const createInvoice = async (linkId: string) => {
        try {
            setIsLoading(true);
            await IntegrationService.CreateInvoiceFromPaymentLink(linkId);
            message.success(GetText("AddPaymentLink_InvoiceCreated"));
            if (props.loadMembers) {
                props.loadMembers.length > 0 ? props.loadMembers(false) : (props.loadMembers as () => void)();
            }
        } catch (error) {
            message.error(GetText("AddPaymentLink_InvoiceCreationError"));
        } finally {
            setIsLoading(false);
        }
    }

    const downloadInvoice = async (linkId: string) => {
        try {
            setIsLoading(true);
            const result = await IntegrationService.DownloadInvoiceFromPaymentLink(linkId);
            const fileName = getFileNameFromContentDisposition(result.contentDisposition, `invoice-${linkId}.pdf`);
            downloadBlobFile(result.blob, fileName);
        } catch (error) {
            message.error(GetText("AddPaymentLink_InvoiceCreationError"));
        } finally {
            setIsLoading(false);
        }
    }

    const downloadReceipt = async (linkId: string) => {
        try {
            setIsLoading(true);
            const result = await IntegrationService.DownloadReceiptFromPaymentLink(linkId);
            const fileName = getFileNameFromContentDisposition(result.contentDisposition, `receipt-${linkId}.pdf`);
            downloadBlobFile(result.blob, fileName);
        } catch (error) {
            message.error(GetText("AddPaymentLink_ReceiptError"));
        } finally {
            setIsLoading(false);
        }
    }

    const deletePaymentLink = async () => {
        if (!props.member.paymentLink) return;
        await IntegrationService.CancelPaymentLink(props.member.paymentLink);
        setOpen(false);
        loadPayments(false);
        if (props.loadMembers) {
            props.loadMembers.length > 0 ? props.loadMembers(false) : (props.loadMembers as () => void)();
        }
        message.success(GetText("AddPaymentLink_LinkDeleted"))
    }

    const onYearChanged = (selectedYear: string) => {
        var yearFound = false;
        payments.forEach((element: WebApiModelsPayment) => {
            if (element.year == selectedYear) {
                yearFound = true;
                form.setFieldValue("amount", element.amount);
                form.setFieldValue("paymentDate", moment(element.paymentDate));
            }
        });

        if (!yearFound) {
            form.setFieldValue("amount", "");
            form.setFieldValue("paymentDate", null);
        }
    }

    const loadPayments = (isFirstLoading: boolean) => {
        if (!props.member.memberId) return;

        if (isFirstLoading) {
            form.setFieldValue("year", getPreviousYear(0));
        }

        MemberService.GetPayments(props.member.memberId).then(result => {
            setPayments(result.manualPayments!);
            setExternalPayments(result.externalPayments!);

            result.manualPayments!.forEach((element: WebApiModelsPayment) => {
                if (element.year == form.getFieldValue("year")) {
                    form.setFieldValue("amount", element.amount);
                    form.setFieldValue("paymentDate", moment(element.paymentDate));
                }
            });
        });
    }


    return (
        <>
            <Modal
                open={open}
                title={GetText("AddPaymentLink_ModalTitle") + " - (" + props.member.uniqueId + ") " + props.member.firstName + " " + props.member.lastName}
                width={700}
                okText={GetText("EditMember_ModalClose")}
                onOk={onCancel}
                onCancel={onCancel}
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                {props.member.isPaidCurrentYear ? (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <p>{GetText("AddPaymentLink_AlreadyPaidFor")} {getCurrentYearAsString()}</p>
                        {props.member.paymentLinkId && (
                            <BookyButton
                                type="default"
                                fullWidth={false}
                                text={GetText("AddPaymentLink_DownloadReceipt")}
                                loading={isLoading}
                                onClick={() => downloadReceipt(props.member.paymentLinkId!)}
                                icon={<FilePdfOutlined />}
                            />
                        )}
                    </Space>
                ) : ""}

                {props.member.paymentLink != null && props.member.isPaidCurrentYear == false ?
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            {GetText("AddPaymentLink_MemberCanUseLink")} {getCurrentYearAsString()}:<br />
                            <a href={props.member.paymentLink} target="_blank">{props.member.paymentLink}</a>
                        </div>
                        <Space>
                            {props.member.paymentLinkId && (
                                props.member.invoiceUrl ? (
                                    <BookyButton
                                        type="default"
                                        fullWidth={false}
                                        text={GetText("AddPaymentLink_DownloadInvoice")}
                                        loading={isLoading}
                                        onClick={() => downloadInvoice(props.member.paymentLinkId!)}
                                        icon={<FilePdfOutlined />}
                                    />
                                ) : (
                                    <BookyButton
                                        type="default"
                                        fullWidth={false}
                                        text={GetText("AddPaymentLink_CreateInvoice")}
                                        loading={isLoading}
                                        onClick={() => createInvoice(props.member.paymentLinkId!)}
                                    />
                                )
                            )}
                            <BookyButton
                                danger
                                fullWidth={false}
                                text={GetText("AddPaymentLink_DeleteButton")}
                                onClick={deletePaymentLink}
                            />
                        </Space>
                        <i>{GetText("AddPaymentLink_DeleteWarning")}</i>
                    </Space>
                    : ""}

                {newlyCreatedUri && props.member.isPaidCurrentYear == false ?
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            {GetText("AddPaymentLink_MemberCanUseLink")} {getCurrentYearAsString()}:<br />
                            <a href={newlyCreatedUri} target="_blank">{newlyCreatedUri}</a>
                        </div>
                        <Space>
                            {paymentLinkId && (
                                props.member.invoiceUrl ? (
                                    <BookyButton
                                        type="default"
                                        fullWidth={false}
                                        text={GetText("AddPaymentLink_DownloadInvoice")}
                                        loading={isLoading}
                                        onClick={() => downloadInvoice(paymentLinkId)}
                                        icon={<FilePdfOutlined />}
                                    />
                                ) : (
                                    <BookyButton
                                        type="default"
                                        fullWidth={false}
                                        text={GetText("AddPaymentLink_CreateInvoice")}
                                        loading={isLoading}
                                        onClick={() => createInvoice(paymentLinkId)}
                                    />
                                )
                            )}
                            <BookyButton
                                danger
                                fullWidth={false}
                                text={GetText("AddPaymentLink_DeleteButton")}
                                onClick={deletePaymentLink}
                            />
                        </Space>
                        <i>{GetText("AddPaymentLink_DeleteWarning")}</i>
                    </Space>
                    : ""}

                {newlyCreatedUri == null && props.member.paymentLink == null && props.member.isPaidCurrentYear == false ?
                    <Form form={form} colon={false} labelAlign={"left"} labelCol={{ span: 8, offset: 0 }} wrapperCol={{ span: 16 }} >
                        <Col span={17}>
                            <BookyInput label={GetText("AddPayment_Description")} onClick={onCreate} name="description" requiredMessage={GetText("AddPaymentLink_DescriptionRequired")} placeholder={GetText("AddPaymentLink_DescriptionPlaceholder")} />

                            <BookyInput type={InputType.number} label={GetText("AddPayment_NoteColumnPlaceHolder")} requiredMessage={GetText("AddPaymentLink_AmountRequired")} onClick={onCreate} name="amount" req placeholder={GetText("AddPayment_NoteColumnPlaceHolder")} />
                            <BookySelectDropDown label={GetText("AddPayment_YearColumn")} name="year" handleChange={onYearChanged} options={getCurrentYearAsOption()} />
                            <Form.Item label={" "}>
                                <BookyButton
                                    type="primary"
                                    fullWidth={false}
                                    text={GetText("AddPaymentLink_CreateButton")}
                                    onClick={onCreate}
                                >
                                </BookyButton>
                            </Form.Item>
                        </Col>
                    </Form> : ""}
            </Modal>
            <LinkOutlined className={props.member.paymentLink != null ? "" : "not-payed"}
                style={{ fontSize: "20px" }}
                type="primary"
                onClick={() => {
                    setOpen(true);
                    loadPayments(true);
                }}
            >
            </LinkOutlined>
        </>
    );
};

export default AddPaymentLink;